import {Component, inject, signal} from '@angular/core';
import {SecteurActiviteService} from '../../core/services/entities/secteur-activite.service';
import {SecteurActiviteDTO} from '../../core/dtos/secteur-activite-dto';
import {ToastService} from '../../core/services/technique/toast.service';
import {InternationalizationService} from '../../core/services/i8n/i8n.service';
import {UtilsService} from '../../core/utils/utils.service';
import {MSG_KEY, MSG_SEVERITY} from '../../core/constants';
import {ResponseWrapper} from '../../core/suppliers/wrappers/response-wrapper';
import {IsDeletableObject} from '../../core/models/is-deletable-object';
import {confirm} from 'devextreme/ui/dialog';
import {ButtonThemeEnum} from '../../shared/ui/button/button-theme-enum';

@Component({
  selector: 'yo-secteurs-activite',
  templateUrl: './secteurs-activite.component.html',
  styleUrls: ['./secteurs-activite.component.scss'],
  standalone: false
})
export class SecteursActiviteComponent {

  protected readonly ButtonThemeEnum = ButtonThemeEnum;

  readonly secteurActiviteSvc = inject(SecteurActiviteService);
  private readonly toastSvc = inject(ToastService);
  private readonly i8nSvc = inject(InternationalizationService);
  readonly utils = inject(UtilsService);

  selectedRows: number[] = [];
  readonly errors = signal<{ infosLine: string }[]>([]);
  readonly displayDeletePopupErrors = signal(false);

  readonly showDialog = signal(false);
  readonly editingDto = signal<SecteurActiviteDTO | null>(null);

  openDialogEdition = (dto: SecteurActiviteDTO | null): void => {
    this.editingDto.set(dto);
    this.showDialog.set(true);
  };

  onSaved = ({dto, isUpdate}: { dto: SecteurActiviteDTO; isUpdate: boolean }): void => {
    this.secteurActiviteSvc.secteursActiviteResource.update(current => {
      const list = current ?? [];
      return isUpdate
        ? list.map(s => s.id === dto.id ? dto : s)
        : [...list, dto];
    });
  };

  calculateSiteValue = (dto: SecteurActiviteDTO): string => dto?.site?.libelle ?? '';

  canDelete = (): boolean => this.selectedRows.length > 0;

  deleteValues = async (): Promise<void> => {
    this.errors.set([]);
    const idsToDelete = [...this.selectedRows];

    const confirmed: boolean = await confirm(
      this.i8nSvc.getLabelFromCode(idsToDelete.length > 1 ? 'CONFIRM_DELETION_PLURAL' : 'CONFIRM_DELETION_SINGULAR', null),
      this.i8nSvc.getLabelFromCode('Suppression', null)
    );

    if (!confirmed) { return; }

    this.secteurActiviteSvc.delete(idsToDelete).subscribe(response => {
      const res: any = (response as ResponseWrapper<IsDeletableObject>).one;
      if (res) {
        if (res.messagesErrorList?.length) {
          this.errors.set(res.messagesErrorList.map(error => ({
            infosLine: this.i8nSvc.getLabelFromCode(error.code, error.args?.split(','))
          })));
          this.displayDeletePopupErrors.set(true);
        } else {
          const msgKey = idsToDelete.length > 1
            ? 'SECTEUR_ACTIVITE_DELETION_COMPLETED_PLURAL'
            : 'SECTEUR_ACTIVITE_DELETION_COMPLETED_SINGULAR';
          this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.SUCCESS,
            this.i8nSvc.getLabelFromCode(msgKey, null));
        }
        this.secteurActiviteSvc.secteursActiviteResource.update(
          current => (current ?? []).filter(s => !idsToDelete.includes(s.id))
        );
      }
      this.selectedRows = [];
    });
  };

  closeErrors = (): void => {
    this.errors.set([]);
    this.displayDeletePopupErrors.set(false);
  };
}
